import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import moment from 'moment';
import { convertCurrency } from '../../../common/helpers/currency-helper';
import { calculateWidth } from '../../../common/helpers/dimension-helper';
import { ITransactionLookupData } from 'src/common/interfaces/dtos/ITransactionLookupData';
import { ReactComponent as Transactions } from '../../../common/media/Transactions.svg';
import { themeColor } from '../../../mui-theme';
import VerticalCenter from '../../../vertical-center/vertical-center';
import { CheckMark } from '../../../search-transactions/components/transaction-list/transaction-list-checkmark';
import { styles } from './transaction-list-styles';
import { useTranslation } from 'react-i18next';

export interface ITransactionListProps {
  transactions: ITransactionLookupData[];
  onTransactionClick: (transaction: ITransactionLookupData) => void;
  selectedTransactionID?: string;
}

const TransactionList = ({
  transactions,
  selectedTransactionID,
  onTransactionClick,
}: ITransactionListProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  return (
    <Grid
      container
      alignContent='flex-start'
      className={classes.component}
      data-testid='transaction-list'
    >
      {transactions.length ? (
        transactions.map(transaction => {
          const transactionId = transaction.transactionId;
          return (
            <Grid item xs={12} key={transactionId}>
              <TransactionItem
                transaction={transaction}
                selected={selectedTransactionID === transaction.transactionId}
                onClick={() => onTransactionClick(transaction)}
              />
            </Grid>
          );
        })
      ) : (
        <Grid item xs={12} className={classes.transactionMessage}>
          <Typography>{t('SearchPanel_NoResults')}</Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default TransactionList;

interface ITransactionItemProps {
  transaction: ITransactionLookupData;
  selected: boolean;
  onClick: () => void;
}

const Item = ({ transaction, selected, onClick }: ITransactionItemProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const { beginDateTime, touchPointId, sequenceNumber, totalAmount } = transaction;
  const transactionTotal = convertCurrency(totalAmount || 0);

  const toDateString = () => {
    return beginDateTime ? moment(beginDateTime).format('MM/DD/YYYY HH:mm') : '';
  };

  return (
    <Grid
      container
      className={classes.transactionItemContainer}
      onClick={onClick}
      style={{
        borderColor: selected ? themeColor.primaryDark : themeColor.secondaryLight,
      }}
      data-testid='transaction-list-item'
    >
      {selected && (
        <div className={classes.transactionItemCheck} data-testid='transaction-list-item-check'>
          <CheckMark />
        </div>
      )}

      <Grid item xs={1}>
        <VerticalCenter justifyContent='center'>
          <Transactions className={classes.transactionImage} />
        </VerticalCenter>
      </Grid>
      <Grid item xs={4}>
        <VerticalCenter justifyContent='flex-start' alignContent='center'>
          <Grid item xs={12}>
            <Typography className={classes.transactionSequenceNumber}>{toDateString()}</Typography>
          </Grid>
          <Grid item xs={12}>
            {' '}
            <Typography
              className={classes.transactionItemName}
              data-testid='transaction-item-tp-id'
            >
              {t('ResumeTransactionViewPOSNumberArgs', { arg: touchPointId })}
            </Typography>
          </Grid>
        </VerticalCenter>
      </Grid>

      <Grid item xs={3}>
        <VerticalCenter justifyContent='flex-start'>
          <Typography className={classes.transactionDate}>{sequenceNumber}</Typography>
        </VerticalCenter>
      </Grid>
      <Grid item xs={3}>
        <VerticalCenter
          justifyContent='flex-end'
          containerStyle={{ paddingRight: calculateWidth(22.1).stringValue }}
        >
          <Typography className={classes.transactionTotal} data-testid='transaction-item-total'>
            {transactionTotal}
          </Typography>
        </VerticalCenter>
      </Grid>
    </Grid>
  );
};

export const TransactionItem = React.memo(Item);
